'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { I18nConfig } from '../types/app-config';

export type TranslationParams = Record<string, string | number | boolean | null | undefined>;
export type TranslateFn = (key: string, params?: TranslationParams) => string;

type TranslationDictionary = Record<string, string>;

const RTL_LANGUAGE_CODES = new Set(['ar', 'he', 'fa', 'ur']);

const LOCALE_STORAGE_KEY = 'runtime-shell:locale';

const DEFAULT_TRANSLATIONS: Record<string, TranslationDictionary> = {
  en: {
    'common.home': 'Home',
    'common.search': 'Search',
    'common.cancel': 'Cancel',
    'common.create': 'Create',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.view': 'View',
    'common.retry': 'Try Again',
    'common.loading': 'Loading...',
    'common.addEntity': 'Add {{entityName}}',
    'common.backToPlural': 'Back to {{entityPlural}}',

    'shell.commandPalettePlaceholder': 'Search pages, entities, and recent items...',
    'shell.openNavigationMenu': 'Open navigation menu',
    'shell.openCommandPalette': 'Open command palette',
    'shell.searchButton': 'Search',
    'shell.commandShortcut': 'Cmd/Ctrl+K',
    'shell.toggleDarkMode': 'Toggle dark mode',
    'shell.signOut': 'Sign out',
    'shell.closeMenu': 'Close menu',
    'shell.activeUsers': 'Active users',
    'shell.locale': 'Locale',

    'path.createEntity': 'Create {{entityName}}',
    'path.editEntity': 'Edit {{entityName}}',
    'path.entityDetails': '{{entityName}} #{{id}}',

    'list.title': '{{entityPlural}}',
    'list.description': 'Manage your {{entityPluralLower}}',
    'list.loading': 'Loading {{entityPluralLower}}...',
    'list.loadError': 'Failed to load {{entityPluralLower}}',
    'list.noDataTitle': 'No {{entityPluralLower}} found',
    'list.empty': 'No {{entityNameLower}} found',
    'list.noDataDescription': 'Get started by creating your first {{entityNameLower}}.',
    'list.searchPlaceholder': 'Search {{entityPluralLower}}...',
    'list.exportSelected': 'Export Selected',
    'list.deleteSelected': 'Delete Selected',
    'list.deletingSelected': 'Deleting...',
    'list.deleteSelectedTitle': 'Delete selected {{entityPluralLower}}',
    'list.deleteSelectedDescription': 'Delete {{count}} selected {{entityPluralLower}}? This action cannot be undone.',
    'list.deletedSelectedSuccess': '{{count}} {{entityPluralLower}} deleted',
    'list.deleteSelectedError': 'Failed to delete selected {{entityPluralLower}}',
    'list.noSelection': 'No {{entityPluralLower}} selected',
    'list.exportSuccess': 'Exported {{count}} {{entityPluralLower}}',
    'list.showingSummary': 'Showing {{shown}} of {{total}}',
    'list.showing': 'Showing {{count}} of {{total}}',
    'list.deleteTitle': 'Delete {{entityName}}',
    'list.deleteDescription': 'Are you sure you want to delete this {{entityNameLower}}? This action cannot be undone.',
    'list.deleteSuccess': '{{entityName}} deleted successfully',
    'list.deleteError': 'Failed to delete {{entityName}}',
    'list.actions.view': 'View',
    'list.actions.edit': 'Edit',
    'list.actions.delete': 'Delete',

    'create.title': 'Create {{entityName}}',
    'create.description': 'Add a new {{entityNameLower}}',
    'create.button': 'Create {{entityName}}',
    'create.creating': 'Creating...',
    'create.success': '{{entityName}} created successfully',
    'create.error': 'Failed to create {{entityName}}',
    'create.permissionDenied': 'You do not have permission to create {{entityPluralLower}}',

    'edit.title': 'Edit {{entityName}}',
    'edit.description': 'Update {{entityNameLower}} details',
    'edit.loading': 'Loading {{entityNameLower}}...',
    'edit.loadError': 'Failed to load {{entityNameLower}}',
    'edit.button': 'Save Changes',
    'edit.saving': 'Saving...',
    'edit.success': '{{entityName}} updated successfully',
    'edit.error': 'Failed to update {{entityName}}',
    'edit.permissionDenied': 'You do not have permission to edit {{entityPluralLower}}',

    'detail.title': '{{entityName}} Details',
    'detail.loading': 'Loading {{entityNameLower}}...',
    'detail.loadError': 'Failed to load {{entityNameLower}}',
    'detail.notFoundError': '{{entityName}} not found.',
    'detail.timeoutError': 'Request timed out. Please try again.',
    'detail.networkError': 'Network request failed. Check your connection and try again.',
    'detail.abortError': 'Request was cancelled. Please try again.',

    'custom.accessDenied.title': 'Access denied',
    'custom.accessDenied.description': 'You do not have permission to view this page.',
    'custom.loadingWidget': 'Loading widget...',
    'custom.widgetDataError': 'Failed to load widget data.',
    'custom.unknownEntity': 'Unknown entity data source.',
    'custom.noChartData': 'No chart data configured.',
    'custom.noActivity': 'No activity yet.',
    'custom.widgetErrorTitle': 'Widget failed to render',
    'custom.widgetErrorDescription': 'There was a problem rendering {{widgetName}}. Try again.',
    'custom.intentErrorTitle': 'Failed to render component',
    'custom.intentErrorDescription': 'The requested page component failed to render. Please try again.',
    'custom.unknownIntent': 'Unknown intent component: {{componentName}}',
    'custom.availableIntents': 'Available: {{intents}}',
    'custom.emptyContent': 'This page has no content configured. Add an `intentComponent` to the page config.',

    'auth.signIn': 'Sign in',
    'auth.enterToken': 'Enter your access token to continue.',
    'auth.enterCredentials': 'Sign in with your email and password.',
    'auth.accessToken': 'Access token',
    'auth.pasteToken': 'Paste token',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.password': 'Password',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.continue': 'Continue',
    'auth.signingIn': 'Signing in...',
    'auth.tokenRequired': 'Access token is required',
    'auth.emailRequired': 'Email is required',
    'auth.passwordRequired': 'Password is required',
    'auth.invalidCredentials': 'Invalid email or password',
    'auth.loginFailed': 'Login failed. Please try again.',
    'auth.createAccount': 'Create an account',
    'auth.signingOut': 'Signing out...',

    'route.notFoundCode': '404',
    'route.notFound': 'Page not found',

    'field.select': 'Select {{label}}',
    'field.searchReference': 'Search {{label}}...',
    'field.yes': 'Yes',
    'field.no': 'No',
    'field.uploadPreview': 'Preview',
    'field.uploaded': 'Uploaded:',
    'field.invalidFileType': 'Invalid file type',
    'field.fileTooLarge': 'File too large',
    'field.uploadFailed': 'Upload failed. Please try again.',
    'field.uploadEndpointMissing': 'Upload endpoint is not configured. Set `config.storage.uploadUrl`.',
    'field.uploadNoUrl': 'Upload completed but no URL was returned by the server.',
    'field.referenceLoadError': 'Failed to load reference options',
  },
};

export interface LocaleContextValue {
  locale: string;
  supportedLocales: string[];
  isLoading: boolean;
  setLocale: (locale: string) => void;
  t: TranslateFn;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);
const FALLBACK_LOCALE_CONTEXT: LocaleContextValue = {
  locale: 'en',
  supportedLocales: ['en'],
  isLoading: false,
  setLocale: () => undefined,
  t: (key, params) => interpolate(DEFAULT_TRANSLATIONS.en?.[key] || key, params),
};

function normalizeLocaleList(config?: I18nConfig): string[] {
  const configuredLocales = [
    ...(config?.supportedLocales || []),
    ...(config?.locales || []),
  ].filter((value): value is string => typeof value === 'string');

  const normalizedDefault = (config?.defaultLocale || 'en').trim() || 'en';
  const normalizedConfigured = configuredLocales
    .map(locale => locale.trim())
    .filter(locale => !!locale);
  const unique = Array.from(new Set(normalizedConfigured));

  if (!unique.includes(normalizedDefault)) {
    unique.unshift(normalizedDefault);
  }

  if (unique.length === 0) {
    unique.push('en');
  }

  return unique;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, key: string) => {
    if (!(key in params)) return '';
    const value = params[key];
    return value === null || value === undefined ? '' : String(value);
  });
}

function normalizeTranslationPayload(payload: unknown, locale: string): TranslationDictionary {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const record = payload as Record<string, unknown>;
  const direct = record.translations ?? record.messages ?? record.dictionary;

  const candidates: unknown[] = [
    direct,
    record[locale],
    payload,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue;
    const dictionary: TranslationDictionary = {};
    Object.entries(candidate as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof value === 'string') {
        dictionary[key] = value;
      }
    });
    if (Object.keys(dictionary).length > 0) {
      return dictionary;
    }
  }

  return {};
}

function resolveTranslationUrl(endpoint: string, locale: string): string {
  if (endpoint.includes(':locale')) {
    return endpoint.replace(':locale', encodeURIComponent(locale));
  }

  try {
    const url = new URL(endpoint, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    url.searchParams.set('locale', locale);
    return url.toString();
  } catch {
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}locale=${encodeURIComponent(locale)}`;
  }
}

export function isRtlLocale(locale: string): boolean {
  try {
    const localeInfo = new Intl.Locale(locale) as Intl.Locale & {
      textInfo?: { direction?: 'ltr' | 'rtl' };
    };
    if (localeInfo.textInfo?.direction) {
      return localeInfo.textInfo.direction === 'rtl';
    }
  } catch {
    // fallback handled below
  }

  const language = locale.split('-')[0]?.toLowerCase();
  return language ? RTL_LANGUAGE_CODES.has(language) : false;
}

export function formatLocaleDate(
  value: Date | number,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, options).format(value);
}

export function formatLocaleNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

export interface LocaleProviderProps {
  children: ReactNode;
  config?: I18nConfig;
}

export function LocaleProvider({ children, config }: LocaleProviderProps) {
  const supportedLocales = useMemo(
    () => normalizeLocaleList(config),
    [config],
  );
  const defaultLocale = supportedLocales[0] || 'en';
  const [locale, setLocaleState] = useState(defaultLocale);
  const [remoteTranslations, setRemoteTranslations] = useState<TranslationDictionary>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!stored) return;
    if (!supportedLocales.includes(stored)) return;
    setLocaleState(stored);
  }, [supportedLocales]);

  const setLocale = useCallback((nextLocale: string) => {
    if (!supportedLocales.includes(nextLocale)) {
      return;
    }

    setLocaleState(nextLocale);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    }
  }, [supportedLocales]);

  useEffect(() => {
    if (!config?.translationsEndpoint) {
      setRemoteTranslations({});
      setIsLoading(false);
      return;
    }

    const endpoint = resolveTranslationUrl(config.translationsEndpoint, locale);
    const abortController = new AbortController();
    setIsLoading(true);

    void fetch(endpoint, { signal: abortController.signal })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load translations (${response.status})`);
        }
        const payload = await response.json() as unknown;
        return normalizeTranslationPayload(payload, locale);
      })
      .then(dictionary => {
        if (!abortController.signal.aborted) {
          setRemoteTranslations(dictionary);
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setRemoteTranslations({});
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [config?.translationsEndpoint, locale]);

  const t = useCallback<TranslateFn>((key, params) => {
    const defaultEnglish = DEFAULT_TRANSLATIONS.en || {};
    const localeDefaults = DEFAULT_TRANSLATIONS[locale] || {};
    const fallbackDefaults = DEFAULT_TRANSLATIONS[defaultLocale] || {};

    const template = remoteTranslations[key]
      || localeDefaults[key]
      || fallbackDefaults[key]
      || defaultEnglish[key]
      || key;

    return interpolate(template, params);
  }, [locale, defaultLocale, remoteTranslations]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    supportedLocales,
    isLoading,
    setLocale,
    t,
  }), [locale, supportedLocales, isLoading, setLocale, t]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  return context || FALLBACK_LOCALE_CONTEXT;
}

export function useTranslator(): TranslateFn {
  const { t } = useLocale();
  return t;
}

export function useTranslation(key: string, params?: TranslationParams): string {
  const t = useTranslator();
  return useMemo(
    () => t(key, params),
    [key, params, t],
  );
}
